import { ButtonRemove, ButtonTertiary } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { UploadFlowGrid } from "panels/content/side-panel/upload-flow/preview-files/UploadFlowGrid"
import { UploadFlowList } from "./UploadFlowList"
import { useUploadFlowContext } from "../useUploadFlowContext"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import * as Icons from "@nova/component-icons"
import { useEffect, useMemo, useRef, useState } from "react"
import { ImageProcessingState, UploadPresentationOptions } from "@nova/constants"
import { AutoProcessController } from "./AutoProcessController"
import { stagedFiles } from "@nova/slice-multifile-upload"
import { selectActiveImageIds } from "features/map/layers/selectors"
import { formatBytes } from "@nova/format"
import { FlightUploadDropbox } from "panels/content/side-panel/upload-flow/components/FlightUploadDropbox"
import { setActiveImageId } from "features/map/layers/slice"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { fileUploadUsage, flightExceededUsage } from "../module"
import { LoadFromLayerButton } from "panels/content/side-panel/upload-flow/LoadFromLayer"

export const PreviewFilesUploads = ({
    viewMode,
    isPaywalled,
    removeSelected,
    isRemoving,
    uploadPresentationOptions = {
        completeStates: [ImageProcessingState.IngestDone],
        uploadingStates: [ImageProcessingState.UploadDone],
    },
}: {
    viewMode: "List" | "Grid"
    isPaywalled?: boolean
    removeSelected: (uploadIds: Record<string, boolean>) => Promise<void>
    isRemoving: boolean
    uploadPresentationOptions?: UploadPresentationOptions
}) => {
    const dispatch = useAppDispatch()
    const {
        portfolioId,
        fireId,
        flightId,
        uploads,
        erroredUploads,
        stageFiles,
        uploadFlight,
        deleteUpload,
        selectFinishedUploadSize,
        selectIsUploading,
        isDemo,
    } = useUploadFlowContext()
    const { isEditor } = useWorkspacePermissions()

    const activeImageIds = useAppSelector(selectActiveImageIds)
    const finishedUploadSize = useAppSelector(selectFinishedUploadSize(flightId))
    const isUploading = useAppSelector(selectIsUploading(flightId))
    const selectedUploadIds = useAppSelector(selectActiveImageIds)

    const containerRef = useRef<HTMLDivElement | null>(null)

    const selectedUploads = useMemo(
        () => uploads.filter((upload) => selectedUploadIds[upload.id]),
        [selectedUploadIds, uploads]
    )

    // Nothing sets UploadNetworkErrorCode, so gating on it never offered a retry. A still-staged
    // file is what retryImages actually needs to re-upload.
    const hasRetryableUpload = useMemo(
        () => erroredUploads.some((upload) => !!stagedFiles[upload.id]),
        [erroredUploads]
    )

    const selectedErroredUploads = useMemo(
        () => erroredUploads.filter((u) => activeImageIds[u.id]),
        [erroredUploads, activeImageIds]
    )

    const retryImages = async () => {
        const selected =
            selectedErroredUploads.length === 0 ? erroredUploads : selectedErroredUploads
        await Promise.all(
            selected.map((e) =>
                dispatch(
                    deleteUpload({
                        setId: flightId,
                        uploadId: e.id,
                    })
                )
            )
        )
        const files: File[] = []
        selected.forEach((e) => {
            files.push(stagedFiles[e.id])
        })
        let cancel = false
        AutoProcessController.emit("new" + flightId)
        AutoProcessController.once("new" + flightId, () => {
            cancel = true
        })
        dispatch(
            stageFiles(portfolioId, fireId, flightId, files, () => {
                if (!cancel) {
                    AutoProcessController.emit(`flightId:${flightId}:uploaded-all`)
                }
            })
        )
        //reset the exceeded usage tracking
        delete flightExceededUsage[flightId]
        dispatch(uploadFlight(flightId))
    }

    const [isOverflowed, setIsOverflowed] = useState(false)
    useEffect(() => {
        const el = containerRef.current
        if (el) {
            setIsOverflowed(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight)
        }
    }, [])

    const localUsage = fileUploadUsage[flightId]
    return (
        <ClickAwayListener
            onClickAway={() => dispatch(setActiveImageId())}
            className="flex flex-col flex-grow min-h-0"
        >
            <ScrollableArea ref={containerRef} className="mb-2 pt-px">
                {viewMode === "Grid" && (
                    <UploadFlowGrid
                        uploads={uploads}
                        hasExceededUploads={isPaywalled}
                        isOverflowed={isOverflowed}
                        removeSelected={() => removeSelected(activeImageIds)}
                        uploadPresentationOptions={uploadPresentationOptions}
                    />
                )}
                {viewMode === "List" && (
                    <UploadFlowList
                        removeSelected={() => removeSelected(activeImageIds)}
                        completeStates={uploadPresentationOptions.completeStates}
                    />
                )}
            </ScrollableArea>
            {hasRetryableUpload && (
                <>
                    <div className="flex gap-2">
                        <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />
                        <div>
                            <h5 className="text-red-500">Upload Failed</h5>
                            <h5 className="text-gray-300">Some files did not finish uploading</h5>
                        </div>
                    </div>
                    <ButtonTertiary
                        onClick={retryImages}
                        className="my-2 w-full"
                        title="Retry"
                        RightIcon={Icons.Return}
                        disabled={isUploading}
                    />
                </>
            )}
            {(selectedUploads.length > 0 || isRemoving) && (
                <ButtonRemove
                    className="w-full mb-2"
                    LeftIcon={Icons.ClearAll}
                    onClick={() => removeSelected(activeImageIds)}
                    title="Remove Selected Images"
                    isTransparent
                    isRemoving={isRemoving}
                />
            )}
            {isUploading && (
                <h5 className="text-gray-100 mb-2">
                    {localUsage
                        ? `${formatBytes(finishedUploadSize)} of ${formatBytes(localUsage)}`
                        : formatBytes(finishedUploadSize)}
                </h5>
            )}
            {!isUploading && !isPaywalled && !isDemo && (
                <div>
                    {/* override mb-2 default class */}
                    <FlightUploadDropbox isAddMoreFiles className="" />
                    {isEditor && <LoadFromLayerButton className="mt-2" />}
                </div>
            )}
        </ClickAwayListener>
    )
}
